/**
 * NativeHome — mobile-first launch screen shown ONLY when running inside the
 * Capacitor shell (iOS / Android). On the web, `HomeRouter` renders the classic
 * `<Home />` marketing page instead.
 *
 * Design intent:
 *   - No hero image, no marketing copy — the app is a companion, not a brochure
 *   - Warm contemplative greeting adjusted to the time of day
 *   - Four large tap targets that cover >90% of daily use
 *   - A discreet "Voir le site complet →" foot link for anyone who needs the
 *     full experience (donations, mécénat, association history…)
 *
 * This screen is what makes PRESENCE feel like a native app to Apple's
 * reviewers (satisfies guideline 4.2 — "minimum functionality").
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import api from '@/lib/api';
import { useI18n } from '@/i18n/I18nContext';
import { useMember } from '@/contexts/MemberAuthContext';
import { pickLocalized } from '@/i18n/translations';

const COPY = {
  fr: {
    morning: 'Bonjour',
    afternoon: 'Bel après-midi',
    evening: 'Belle soirée',
    subtitle: 'Que la journée vous soit douce.',
    subtitleNamed: (name) => `${name}, que la journée vous soit douce.`,
    quick: 'ACCÈS RAPIDES',
    a_agenda: 'Mon agenda',
    a_agenda_hint: 'Offices, événements & rendez-vous',
    a_scan: 'Scanner un billet',
    a_scan_hint: 'Check-in Fêtes 2027',
    a_forum: 'Communauté',
    a_forum_hint: 'Forum, covoiturage, hébergements',
    a_prefs: 'Notifications',
    a_prefs_hint: 'Rappels & annonces',
    upcoming: 'À VENIR AU PRIEURÉ',
    empty: 'Aucun événement dans les prochains jours.',
    all_events: 'Voir tous les événements',
    full_site: 'Voir le site complet →',
    login: 'Se connecter à mon espace',
  },
  en: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    subtitle: 'May your day be gentle.',
    subtitleNamed: (name) => `${name}, may your day be gentle.`,
    quick: 'QUICK ACTIONS',
    a_agenda: 'My agenda',
    a_agenda_hint: 'Offices, events & appointments',
    a_scan: 'Scan a ticket',
    a_scan_hint: 'Fêtes 2027 check-in',
    a_forum: 'Community',
    a_forum_hint: 'Forum, carpool, housing',
    a_prefs: 'Notifications',
    a_prefs_hint: 'Reminders & announcements',
    upcoming: 'COMING UP AT THE PRIORY',
    empty: 'No events in the coming days.',
    all_events: 'See all events',
    full_site: 'See the full site →',
    login: 'Sign in to my space',
  },
};

function greetingKey() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

const CINNABAR = '#7b1f2b';
const GOLD = '#b89a5c';
const PARCHMENT = '#f6f0e2';
const INK = '#1e1a15';

function TileLink({ to, label, hint, icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block border p-5 transition-transform active:scale-[0.98]"
      style={{
        background: '#ffffff',
        borderColor: 'rgba(184, 154, 92, 0.35)',
        color: INK,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          aria-hidden="true"
          className="w-11 h-11 flex items-center justify-center text-xl font-heading"
          style={{
            background: PARCHMENT,
            color: CINNABAR,
            border: `1px solid ${GOLD}`,
          }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg leading-tight">{label}</div>
          <div
            className="font-body text-xs mt-1 truncate"
            style={{ color: 'var(--muted)' }}
          >
            {hint}
          </div>
        </div>
        <span
          aria-hidden="true"
          className="text-lg mt-1 transition-transform group-hover:translate-x-0.5"
          style={{ color: GOLD }}
        >
          ›
        </span>
      </div>
    </Link>
  );
}

export default function NativeHome() {
  const { locale } = useI18n();
  const l = locale === 'fr' ? 'fr' : 'en';
  const t = COPY[l];
  const { member } = useMember();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/events?limit=3')
      .then((r) => {
        if (cancelled) return;
        // Sort by date ascending, keep first 3 that are in the future or today
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = (r.data || [])
          .filter((e) => (e.date || '') >= today)
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
          .slice(0, 3);
        setEvents(upcoming);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const greetingWord = t[greetingKey()];
  const firstName = (member?.name || '').split(' ')[0];
  const subtitle = firstName ? t.subtitleNamed(firstName) : t.subtitle;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(l === 'fr' ? 'fr-FR' : 'en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      data-testid="native-home"
      className="min-h-screen"
      style={{ background: PARCHMENT }}
    >
      {/* GREETING BAND ─────────────────────────────────── */}
      <header
        className="px-5 pt-14 pb-8"
        style={{
          background: `linear-gradient(180deg, ${PARCHMENT} 0%, #ede4d0 100%)`,
        }}
      >
        <div
          className="font-caps text-[0.68rem] mb-2"
          style={{ color: GOLD, letterSpacing: '0.28em' }}
        >
          PRIEURÉ SAINT LÉONARD
        </div>
        <h1
          className="font-heading leading-tight"
          style={{ color: INK, fontSize: '2rem' }}
        >
          {greetingWord}
        </h1>
        <p
          className="font-body italic mt-1 text-sm"
          style={{ color: 'var(--muted)' }}
        >
          {subtitle}
        </p>
      </header>

      {/* QUICK ACTIONS ──────────────────────────────────── */}
      <section className="px-5 pt-6">
        <div
          className="font-caps text-[0.65rem] mb-3"
          style={{ color: GOLD, letterSpacing: '0.25em' }}
        >
          {t.quick}
        </div>
        <div className="grid grid-cols-1 gap-3">
          <TileLink
            to={member ? '/espace/disponibilites' : '/espace/connexion'}
            label={t.a_agenda}
            hint={t.a_agenda_hint}
            icon="✦"
            testid="native-tile-agenda"
          />
          <TileLink
            to="/scan"
            label={t.a_scan}
            hint={t.a_scan_hint}
            icon="◇"
            testid="native-tile-scan"
          />
          <TileLink
            to={member ? '/espace/forum' : '/espace/connexion'}
            label={t.a_forum}
            hint={t.a_forum_hint}
            icon="◈"
            testid="native-tile-forum"
          />
          <TileLink
            to={member ? '/espace/profil' : '/espace/connexion'}
            label={t.a_prefs}
            hint={t.a_prefs_hint}
            icon="✧"
            testid="native-tile-prefs"
          />
        </div>
      </section>

      {/* UPCOMING EVENTS ─────────────────────────────────── */}
      <section className="px-5 pt-10 pb-6">
        <div
          className="font-caps text-[0.65rem] mb-3"
          style={{ color: GOLD, letterSpacing: '0.25em' }}
        >
          {t.upcoming}
        </div>
        {loading ? (
          <p
            className="font-body italic text-sm py-4"
            style={{ color: 'var(--muted)' }}
          >
            …
          </p>
        ) : events.length === 0 ? (
          <p
            className="font-body italic text-sm py-4"
            style={{ color: 'var(--muted)' }}
            data-testid="native-events-empty"
          >
            {t.empty}
          </p>
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/evenements/${e.id}`}
                  data-testid={`native-event-${e.id}`}
                  className="block border-l-2 pl-3 py-2 active:opacity-70"
                  style={{ borderColor: CINNABAR }}
                >
                  <div
                    className="font-caps text-[0.6rem]"
                    style={{ color: CINNABAR, letterSpacing: '0.2em' }}
                  >
                    {fmtDate(e.date)}
                    {e.time ? ` — ${e.time}` : ''}
                  </div>
                  <div
                    className="font-heading text-base mt-0.5 leading-snug"
                    style={{ color: INK }}
                  >
                    {pickLocalized(e.title, locale)}
                  </div>
                  {e.location && (
                    <div
                      className="font-body text-xs mt-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      {pickLocalized(e.location, locale)}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/evenements"
          data-testid="native-all-events"
          className="inline-block font-caps text-[0.68rem] mt-5"
          style={{ color: CINNABAR, letterSpacing: '0.22em' }}
        >
          {t.all_events} ›
        </Link>
      </section>

      {/* FOOTER ────────────────────────────────────────── */}
      <footer className="px-5 py-8 mt-4 border-t"
              style={{ borderColor: 'rgba(184, 154, 92, 0.3)' }}>
        {!member && (
          <Link
            to="/espace/connexion"
            data-testid="native-login"
            className="block text-center py-3 mb-4 font-body tracking-wider text-sm"
            style={{
              background: CINNABAR,
              color: '#fff',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {t.login}
          </Link>
        )}
        <Link
          to="/accueil"
          data-testid="native-full-site"
          className="block text-center font-body text-xs"
          style={{ color: 'var(--muted)' }}
        >
          {t.full_site}
        </Link>
      </footer>
    </div>
  );
}
